if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.moveBeforeRecommendationSection();
      this.setupGalleryWidthSync();

      this.storageKey = this.dataset.storageKey || 'recently-viewed-products-v1';
      this.displayLimit = 9;
      this.storageLimit = this.parsePositiveInteger(this.dataset.storageLimit, 12);
      this.list = this.querySelector('[data-recently-viewed-list]');
      this.currentProduct = this.readCurrentProduct();

      if (!this.list || !this.currentProduct?.id) return;

      this.setupCarouselDrag();

      const history = this.readHistory();
      const previousProducts = history
        .filter((item) => String(item.id) !== String(this.currentProduct.id))
        .slice(0, this.displayLimit);

      this.render(previousProducts);
      this.saveCurrentProduct(history);
    }

    disconnectedCallback() {
      this.galleryResizeObserver?.disconnect();
      window.removeEventListener('resize', this.handleViewportResize);
      cancelAnimationFrame(this.gallerySyncFrame);
      this.stopCarouselMomentum();

      if (this.list) {
        this.list.removeEventListener('pointerdown', this.handleCarouselPointerDown);
        this.list.removeEventListener('pointermove', this.handleCarouselPointerMove);
        this.list.removeEventListener('pointerup', this.handleCarouselPointerUp);
        this.list.removeEventListener('pointercancel', this.handleCarouselPointerCancel);
        this.list.removeEventListener('lostpointercapture', this.handleCarouselLostPointerCapture);
        this.list.removeEventListener('click', this.handleCarouselClick, true);
        this.list.removeEventListener('dragstart', this.preventNativeDrag);
      }
    }

    moveBeforeRecommendationSection() {
      const targetSuffix = this.dataset.targetSectionSuffix;
      let targetSection = null;

      if (targetSuffix && /^[A-Za-z0-9_-]+$/.test(targetSuffix)) {
        targetSection = document.querySelector(`[id^="shopify-section-"][id$="__${targetSuffix}"]`);
      }

      if (!targetSection) {
        targetSection = this.findRecommendationSectionByHeading();
      }

      if (!targetSection || targetSection.contains(this)) return;

      const previousInstance = document.querySelector('recently-viewed-products[data-relocated="true"]');
      if (previousInstance && previousInstance !== this) {
        previousInstance.remove();
      }

      targetSection.before(this);
      this.dataset.relocated = 'true';
    }

    findRecommendationSectionByHeading() {
      const headings = document.querySelectorAll('h1, h2, h3');

      for (const heading of headings) {
        const text = heading.textContent.trim().replace(/\s+/g, ' ').toLowerCase();
        if (text === 'you may also like') {
          return heading.closest('.shopify-section');
        }
      }

      return null;
    }

    setupGalleryWidthSync() {
      this.galleryWrapper = document.querySelector('.product__media-wrapper');
      if (!this.galleryWrapper) return;

      this.handleViewportResize = () => this.scheduleGalleryWidthSync();
      window.addEventListener('resize', this.handleViewportResize, { passive: true });

      if ('ResizeObserver' in window) {
        this.galleryResizeObserver = new ResizeObserver(() => this.scheduleGalleryWidthSync());
        this.galleryResizeObserver.observe(this.galleryWrapper);
      }

      this.scheduleGalleryWidthSync();
    }

    scheduleGalleryWidthSync() {
      cancelAnimationFrame(this.gallerySyncFrame);
      this.gallerySyncFrame = requestAnimationFrame(() => this.syncWidthToGallery());
    }

    syncWidthToGallery() {
      if (!this.galleryWrapper) return;

      if (!window.matchMedia('(min-width: 750px)').matches) {
        this.classList.remove('recently-viewed-before-gallery--gallery-width');
        this.style.removeProperty('--recently-viewed-gallery-width');
        this.style.removeProperty('--recently-viewed-gallery-left');
        return;
      }

      const galleryRect = this.galleryWrapper.getBoundingClientRect();
      if (galleryRect.width <= 0) return;

      this.style.setProperty('--recently-viewed-gallery-width', `${galleryRect.width}px`);
      this.style.setProperty('--recently-viewed-gallery-left', `${galleryRect.left + window.scrollX}px`);
      this.classList.add('recently-viewed-before-gallery--gallery-width');
    }

    setupCarouselDrag() {
      this.carouselDragThreshold = 5;
      this.carouselMomentumFriction = 0.95;
      this.carouselMomentumMinLaunch = 1.2;
      this.carouselMomentumMinVelocity = 0.3;
      this.carouselMomentumFrame = null;
      this.suppressCarouselClick = false;

      this.carouselDragState = {
        active: false,
        moved: false,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
        velocity: 0,
        lastX: 0,
        lastTime: 0,
      };

      this.handleCarouselPointerDown = this.onCarouselPointerDown.bind(this);
      this.handleCarouselPointerMove = this.onCarouselPointerMove.bind(this);
      this.handleCarouselPointerUp = this.onCarouselPointerUp.bind(this);
      this.handleCarouselPointerCancel = this.onCarouselPointerCancel.bind(this);
      this.handleCarouselLostPointerCapture = this.onCarouselLostPointerCapture.bind(this);
      this.handleCarouselClick = this.onCarouselClick.bind(this);
      this.preventNativeDrag = (event) => event.preventDefault();

      this.list.addEventListener('pointerdown', this.handleCarouselPointerDown);
      this.list.addEventListener('pointermove', this.handleCarouselPointerMove);
      this.list.addEventListener('pointerup', this.handleCarouselPointerUp);
      this.list.addEventListener('pointercancel', this.handleCarouselPointerCancel);
      this.list.addEventListener('lostpointercapture', this.handleCarouselLostPointerCapture);
      this.list.addEventListener('click', this.handleCarouselClick, true);
      this.list.addEventListener('dragstart', this.preventNativeDrag);
    }

    onCarouselPointerDown(event) {
      if (
        event.pointerType !== 'mouse' ||
        !event.isPrimary ||
        event.button !== 0 ||
        this.list.scrollWidth <= this.list.clientWidth + 1
      ) {
        return;
      }

      this.stopCarouselMomentum();
      this.suppressCarouselClick = false;

      const state = this.carouselDragState;
      state.active = true;
      state.moved = false;
      state.pointerId = event.pointerId;
      state.startX = event.clientX;
      state.startScrollLeft = this.list.scrollLeft;
      state.velocity = 0;
      state.lastX = event.clientX;
      state.lastTime = event.timeStamp || performance.now();
    }

    onCarouselPointerMove(event) {
      const state = this.carouselDragState;
      if (!state.active || event.pointerId !== state.pointerId) return;

      const travelled = event.clientX - state.startX;

      if (!state.moved) {
        if (Math.abs(travelled) < this.carouselDragThreshold) return;

        state.moved = true;
        this.list.classList.add('is-dragging');
        this.list.style.scrollBehavior = 'auto';

        if (this.list.setPointerCapture) {
          this.list.setPointerCapture(state.pointerId);
        }
      }

      const now = event.timeStamp || performance.now();
      const elapsed = now - state.lastTime;

      if (elapsed > 0) {
        const perFrame = ((event.clientX - state.lastX) / elapsed) * 16.67;
        state.velocity = state.velocity * 0.7 + perFrame * 0.3;
        state.lastX = event.clientX;
        state.lastTime = now;
      }

      event.preventDefault();
      this.list.scrollLeft = state.startScrollLeft - travelled;
    }

    onCarouselPointerUp(event) {
      this.finishCarouselDrag(event, true);
    }

    onCarouselPointerCancel(event) {
      this.finishCarouselDrag(event, true);
    }

    onCarouselLostPointerCapture(event) {
      this.finishCarouselDrag(event, false);
    }

    finishCarouselDrag(event, releaseCapture) {
      const state = this.carouselDragState;
      if (!state?.active || event.pointerId !== state.pointerId) return;

      const didMove = state.moved;
      const pointerId = state.pointerId;
      const velocity = state.velocity;

      state.active = false;
      state.moved = false;
      state.pointerId = null;
      this.list.classList.remove('is-dragging');
      this.list.style.scrollBehavior = '';

      if (releaseCapture && this.list.hasPointerCapture?.(pointerId)) {
        this.list.releasePointerCapture(pointerId);
      }

      if (didMove) {
        this.suppressCarouselClick = true;
        this.startCarouselMomentum(velocity);
      }
    }

    startCarouselMomentum(initialVelocity) {
      if (Math.abs(initialVelocity) < this.carouselMomentumMinLaunch) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      let velocity = initialVelocity;
      this.list.style.scrollBehavior = 'auto';
      this.list.classList.add('is-gliding');

      const glide = () => {
        const before = this.list.scrollLeft;
        this.list.scrollLeft = before - velocity;

        if (Math.round(this.list.scrollLeft) === Math.round(before)) {
          this.stopCarouselMomentum();
          return;
        }

        velocity *= this.carouselMomentumFriction;

        if (Math.abs(velocity) < this.carouselMomentumMinVelocity) {
          this.stopCarouselMomentum();
          return;
        }

        this.carouselMomentumFrame = requestAnimationFrame(glide);
      };

      this.carouselMomentumFrame = requestAnimationFrame(glide);
    }

    stopCarouselMomentum() {
      if (this.carouselMomentumFrame !== null) {
        cancelAnimationFrame(this.carouselMomentumFrame);
        this.carouselMomentumFrame = null;
      }

      if (this.list) {
        this.list.classList.remove('is-gliding');
        this.list.style.scrollBehavior = '';
      }
    }

    onCarouselClick(event) {
      if (!this.suppressCarouselClick) return;

      this.suppressCarouselClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    parsePositiveInteger(value, fallback) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    readCurrentProduct() {
      const dataNode = this.querySelector('[data-recently-viewed-current-product]');
      if (!dataNode) return null;

      try {
        return JSON.parse(dataNode.textContent);
      } catch (error) {
        console.warn('Recently viewed: current product data could not be parsed.', error);
        return null;
      }
    }

    readHistory() {
      try {
        const stored = window.localStorage.getItem(this.storageKey);
        const parsed = stored ? JSON.parse(stored) : [];
        return Array.isArray(parsed) ? parsed.filter(this.isValidProduct) : [];
      } catch (error) {
        console.warn('Recently viewed: saved history could not be read.', error);
        return [];
      }
    }

    isValidProduct(item) {
      return Boolean(item && item.id && item.title && item.url);
    }

    saveCurrentProduct(history) {
      const updatedHistory = [
        this.currentProduct,
        ...history.filter((item) => String(item.id) !== String(this.currentProduct.id)),
      ].slice(0, this.storageLimit);

      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(updatedHistory));
      } catch (error) {
        console.warn('Recently viewed: current product could not be saved.', error);
      }
    }

    render(products) {
      this.list.replaceChildren();

      if (!products.length) {
        this.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();
      products.forEach((product) => fragment.appendChild(this.createCard(product)));
      this.list.appendChild(fragment);
      this.hidden = false;
    }

    createCard(product) {
      const item = document.createElement('div');
      item.className = 'recently-viewed-before-gallery__item';
      item.setAttribute('role', 'listitem');

      const link = document.createElement('a');
      link.className = 'recently-viewed-before-gallery__card full-unstyled-link';
      link.href = product.url;
      link.draggable = false;
      link.setAttribute('aria-label', product.title);

      const media = document.createElement('div');
      media.className = 'recently-viewed-before-gallery__media';

      if (product.image) {
        const image = document.createElement('img');
        image.className = 'recently-viewed-before-gallery__image';
        image.src = product.image;
        image.alt = product.imageAlt || product.title;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.draggable = false;
        image.width = 300;
        image.height = 300;
        media.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'recently-viewed-before-gallery__placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        media.appendChild(placeholder);
      }

      const details = document.createElement('div');
      details.className = 'recently-viewed-before-gallery__details';

      const title = document.createElement('span');
      title.className = 'recently-viewed-before-gallery__title';
      title.textContent = product.title;
      details.appendChild(title);

      if (product.price) {
        const priceRow = document.createElement('span');
        priceRow.className = 'recently-viewed-before-gallery__prices';

        const price = document.createElement('span');
        price.className = 'recently-viewed-before-gallery__price';
        price.textContent = product.price;
        priceRow.appendChild(price);

        if (product.compareAtPrice) {
          const compareAtPrice = document.createElement('s');
          compareAtPrice.className = 'recently-viewed-before-gallery__compare-price';
          compareAtPrice.textContent = product.compareAtPrice;
          priceRow.appendChild(compareAtPrice);
        }

        details.appendChild(priceRow);
      }

      link.append(media, details);
      item.appendChild(link);
      return item;
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
